import { generateClient } from 'aws-amplify/api';
import {
  GET_WORD_OF_THE_DAY_QUERY,
  GET_RANDOM_WORDS_QUERY,
  GET_WORD_BY_SLUG_QUERY,
  getCurrentLanguage,
} from '../constants';
import { ArabicWord, WordOfTheDayResponse, RandomWordsResponse } from '../types';

const client = generateClient();

// Fetch word of the day using IAM authentication
export const fetchWordOfTheDay = async (date?: string): Promise<ArabicWord> => {
  try {
    const wordLanguage = getCurrentLanguage();
    const variables = { wordLanguage, ...(date && { date }) };
    
    // Use simplified client.graphql approach
    const response = await client.graphql({
      query: GET_WORD_OF_THE_DAY_QUERY,
      variables: variables,
      authMode: 'iam'
    }) as { data: WordOfTheDayResponse };
    
    const wordData = response.data.getWordOfTheDay;
    if (!wordData) {
      throw new Error('No word available for the requested date');
    }
    
    return wordData;
  } catch (error) {
    console.error('Error fetching word:', error);
    throw error;
  }
};

// Fetch random words for quiz using IAM authentication
export const fetchRandomWords = async (startDate: string): Promise<ArabicWord[]> => {
  try {
    const wordLanguage = getCurrentLanguage();
    const variables = { startDate, wordLanguage };
    
    // Use simplified client.graphql approach
    const response = await client.graphql({
      query: GET_RANDOM_WORDS_QUERY,
      variables: variables,
      authMode: 'iam'
    }) as { data: RandomWordsResponse };
    
    const wordsData = response.data.getRandomWords;
    if (!wordsData || wordsData.length === 0) {
      throw new Error('No words available for quiz');
    }
    
    return wordsData;
  } catch (error) {
    console.error('Error fetching random words:', error);
    throw error;
  }
};

interface WordBySlugResponse {
  getWordBySlug: ArabicWord | null;
}

// Resolve a URL slug to a word via a single indexed lookup (wordLanguage-slug-index).
// Returns null when no word has that slug for the current language.
export const fetchWordBySlug = async (slug: string): Promise<ArabicWord | null> => {
  const wordLanguage = getCurrentLanguage();

  const response = (await client.graphql({
    query: GET_WORD_BY_SLUG_QUERY,
    variables: { slug, wordLanguage },
    authMode: 'iam',
  })) as { data: WordBySlugResponse };

  return response.data.getWordBySlug ?? null;
};